import type { FileTransferConfig, DownloadProgressCallback } from "./types";

/**
 * Downloads a file from the API with progress tracking using ReadableStream.
 * This follows industry best practices for file downloads with progress tracking.
 *
 * Based on MDN and JavaScript.info recommendations for streaming downloads:
 * - Memory efficient (processes chunks incrementally)
 * - Progress tracking capability
 * - Handles compressed content properly
 * - Modern browser standard (Streams API)
 *
 * @param fileId - The file ID to download
 * @param config - Configuration with API URL, auth token, and error handling
 * @param callbacks - Progress, completion, and error callbacks
 * @returns Promise<Blob> - The downloaded file as a Blob
 */
export async function downloadFileWithProgress(
  fileId: string,
  config: FileTransferConfig,
  callbacks: DownloadProgressCallback
): Promise<Blob> {
  const { onProgress, onComplete, onError, onHeaders, signal } = callbacks;

  try {
    // Fetch file from API with authentication and cancellation support
    const response = await fetch(`${config.apiBaseUrl}/v1/files/${fileId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${config.getAccessToken()}`
      },
      signal // Pass abort signal for cancellation
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Call headers callback before processing body
    onHeaders?.(response.headers);

    // Get total size from Content-Length header
    // Note: May be undefined if content is compressed (Content-Encoding: gzip)
    const contentLength = response.headers.get("Content-Length");
    const total = contentLength ? parseInt(contentLength, 10) : 0;

    // Get Content-Type to ensure proper MIME type for the blob
    const contentType =
      response.headers.get("Content-Type") || "application/octet-stream";

    if (!response.body) {
      throw new Error("Response body is null");
    }

    // Read stream with progress tracking
    const reader = response.body.getReader();
    const chunks: BlobPart[] = [];
    let loaded = 0;
    let lastProgressUpdate = 0;
    const PROGRESS_THROTTLE_MS = 100; // Throttle updates for performance

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      chunks.push(value);
      loaded += value.length;

      // Calculate percentage (0 if total is unknown/compressed)
      const percentage = total > 0 ? (loaded / total) * 100 : 0;

      // Throttle progress updates for better performance
      const now = Date.now();
      if (
        now - lastProgressUpdate > PROGRESS_THROTTLE_MS ||
        percentage === 100
      ) {
        onProgress(loaded, total, percentage);
        lastProgressUpdate = now;
      }
    }

    // Final progress update to ensure 100% is reached
    if (total > 0) {
      onProgress(loaded, total, 100);
    }

    // Combine chunks into single blob with proper MIME type
    const blob = new Blob(chunks, { type: contentType });

    // Call completion callback
    onComplete?.(blob);

    return blob;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    config.onError?.(err);
    onError?.(err);
    throw err;
  }
}

/**
 * Formats bytes to human-readable string (e.g., "1.5 MB")
 *
 * @param bytes - Number of bytes
 * @returns Formatted string (e.g., "1.5 MB", "523 KB", "45 Bytes")
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) {
    return "0 Bytes";
  }

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Triggers browser download from blob.
 * Creates a temporary anchor element, clicks it, and cleans up.
 * Includes proper memory cleanup with URL.revokeObjectURL().
 *
 * @param blob - The file blob to download
 * @param filename - The filename to save as
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Cleanup blob URL after a delay to ensure download starts
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 100);
}
